import { useState, useCallback } from "react";
import apiService from "../../../../service/Api";
import { Testimonial } from "../types/testimonials";

export const TESTIMONIALS_PAGE_SIZE = 10;

interface PaginatedTestimonials {
  count: number;
  results: Testimonial[];
}

interface UseTestimonialsReturn {
  testimonials: Testimonial[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  fetchTestimonials: (page?: number) => Promise<void>;
  saveTestimonial: (
    testimonial: Omit<Testimonial, "id" | "created_at">,
  ) => Promise<Testimonial>;
  updateTestimonial: (
    id: string,
    updates: Partial<Testimonial>,
  ) => Promise<void>;
  deleteTestimonial: (id: string) => Promise<void>;
}

export const useTestimonials = (): UseTestimonialsReturn => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTestimonials = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await apiService.get<
        Testimonial[] | PaginatedTestimonials
      >("/api/configurations/testimonials/", {
        page,
        page_size: TESTIMONIALS_PAGE_SIZE,
      });
      const results = Array.isArray(data) ? data : (data?.results ?? []);
      setTestimonials(results);
      setTotalCount(Array.isArray(data) ? data.length : (data?.count ?? 0));
    } catch (err: unknown) {
      setTestimonials([]);
      setTotalCount(0);
      setError(
        (err as { message?: string })?.message ||
          "Failed to fetch testimonials",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const saveTestimonial = useCallback(
    async (
      testimonial: Omit<Testimonial, "id" | "created_at">,
    ): Promise<Testimonial> => {
      try {
        const { data } = await apiService.post<Testimonial>(
          "/api/configurations/testimonials/",
          testimonial,
        );
        setTestimonials((prev) => [...prev, data]);
        return data;
      } catch (err: unknown) {
        setError(
          (err as { message?: string })?.message ||
            "Failed to save testimonials",
        );
        throw err;
      }
    },
    [],
  );

  const updateTestimonial = useCallback(
    async (id: string, updates: Partial<Testimonial>) => {
      try {
        const { data } = await apiService.put<Testimonial>(
          `/api/configurations/testimonials/${id}/`,
          updates,
        );
        setTestimonials((prev) => prev.map((t) => (t.id === id ? data : t)));
      } catch (err: unknown) {
        setError(
          (err as { message?: string })?.message ||
            "Failed to update testimonials",
        );
        throw err;
      }
    },
    [],
  );

  const deleteTestimonial = useCallback(async (id: string) => {
    try {
      await apiService.delete(`/api/configurations/testimonials/${id}/`);
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
    } catch (err: unknown) {
      setError(
        (err as { message?: string })?.message ||
          "Failed to delete testimonials",
      );
      throw err;
    }
  }, []);

  return {
    testimonials,
    totalCount,
    loading,
    error,
    fetchTestimonials,
    saveTestimonial,
    updateTestimonial,
    deleteTestimonial,
  };
};
